/**
 * src/pages/reports/_chartSizing.js — how tall a horizontal bar chart has to
 * be before its category labels stop printing on top of each other.
 *
 * Card P227, from Daniel's first real PDF (Patient Acquisition 2.0, 1 Nov).
 *
 * ⛔⛔ THE DEFECT, WITH ITS ARITHMETIC. CeoCharts.js:462 gave BarByStage a
 * FIXED height of 240px whatever it was asked to draw, and BarByRep:413 a
 * fixed 220. The live report had THIRTEEN stages, several with names long
 * enough to wrap onto two lines ("Qualified - Interested", "Treatment
 * Recommended & Presented"). 240 ÷ 13 is 18.5px per row; two lines of 11px
 * type need about 24. So neighbouring labels overlapped, and in print that
 * reads exactly as Daniel described it: double-printed lettering.
 *
 * ⚠⚠ IT IS NOT A PRINT BUG AND NEVER WAS. The same overlap happens on screen
 * whenever the panel is narrow or the pipeline has many stages — the PDF just
 * made it impossible to ignore, because paper does not scroll and nobody can
 * hover a tooltip on it.
 *
 * ⛔ THE HEIGHT IS COMPUTED FROM THE DATA, NOT GUESSED. A bigger constant
 * would have fixed this report and broken on the first pipeline with twenty
 * stages. Rows × the space each row's label actually needs is the only form
 * of this that cannot be outgrown.
 */

/** Row geometry, in CSS pixels. */
export const ROW = Object.freeze({
  /** No row is ever thinner than this, however short its label. */
  min: 28,
  /** One line of the 11px tick type, with its leading. */
  lineHeight: 14,
  /** Breathing room inside a row, so two full rows do not touch. */
  pad: 10,
  /** Axis labels, the caveat line and the panel's own padding. */
  chrome: 48,
});

/**
 * How many lines a category label will take at a given axis width.
 *
 * ⚠ AN ESTIMATE, AND DELIBERATELY A PESSIMISTIC ONE. The exact answer needs
 * a text measurement that does not exist in jsdom and is not worth a canvas
 * at render time; 5.8px per character at 11px Helvetica is close for mixed
 * case and errs toward MORE lines, which errs toward a taller chart. Being
 * wrong in that direction costs whitespace; being wrong the other way is the
 * bug this file exists to remove.
 */
export function labelLines(text, axisWidth, charWidth = 5.8) {
  const s = String(text == null ? '' : text).trim();
  if (!s) return 1;
  const usable = Math.max(24, (Number(axisWidth) || 120) - 12);
  const perLine = Math.max(4, Math.floor(usable / charWidth));
  if (s.length <= perLine) return 1;

  /*
   * ⚠ WRAPPING HAPPENS AT SPACES. "Treatment Recommended & Presented" breaks
   * into three plausible lines; a 30-character single word cannot break at
   * all and takes one (over)long line. Counting characters ÷ perLine would
   * claim two lines for a word that renders as one.
   */
  const words = s.split(/\s+/);
  let lines = 1;
  let len = 0;
  for (const w of words) {
    const add = len === 0 ? w.length : w.length + 1;
    if (len + add > perLine && len > 0) { lines += 1; len = w.length; }
    else len += add;
  }
  return lines;
}

/** The height one row needs, given how many lines its label takes. */
export function rowHeight(lines) {
  return Math.max(ROW.min, (Number(lines) || 1) * ROW.lineHeight + ROW.pad);
}

/**
 * The height a horizontal bar chart needs.
 *
 * @param {string[]} labels      the category labels, in order
 * @param {number}   axisWidth   the YAxis width the chart is drawn with
 * @param {object}   opts        { min, max }
 */
export function barChartHeight(labels, axisWidth, opts = {}) {
  const list = Array.isArray(labels) ? labels : [];
  const min = opts.min || 160;
  /*
   * ⛔ A CAP, BECAUSE A PIPELINE CAN HAVE FIFTY STAGES. Past the cap the
   * chart scrolls on screen and breaks across pages in print, which is worse
   * than a tall chart but far better than an unreadable one — and the
   * truncation below keeps each individual row legible either way.
   */
  const max = opts.max || 900;
  if (list.length === 0) return min;

  const total = list.reduce(
    (sum, l) => sum + rowHeight(labelLines(l, axisWidth)), ROW.chrome);
  return Math.min(max, Math.max(min, Math.round(total)));
}

/**
 * Shorten a label that cannot fit in any reasonable number of lines.
 *
 * ⛔ THE FULL NAME IS NOT LOST — callers put it in the tooltip. This only
 * stops one pathological stage name from making every row in the chart tall.
 */
export function truncateLabel(text, axisWidth, maxLines = 2, charWidth = 5.8) {
  const s = String(text == null ? '' : text).trim();
  const usable = Math.max(24, (Number(axisWidth) || 120) - 12);
  const perLine = Math.max(4, Math.floor(usable / charWidth));
  const budget = perLine * maxLines;
  if (s.length <= budget) return s;
  return `${s.slice(0, Math.max(1, budget - 1)).trimEnd()}…`;
}

/**
 * The end-of-bar label for a horizontal bar chart.
 *
 * ⛔⛔ CARD P227 C — IT CARRIES THE MEASURE THE BAR IS DRAWN FROM. The label
 * used to be the opportunity COUNT while the bar's LENGTH was money, so
 * "Rep One 180" drew a shorter bar than "Rep Two 134" and read as an
 * arithmetic error. Two numbers for one bar, only one of them labelled, is a
 * chart arguing with itself.
 *
 * ⚠ Exported and pure so it can be asserted directly: recharts' LabelList
 * does not render in jsdom, and a red arm proved that an arm checking only
 * the panel's caveat text stays green when the label reverts to the count.
 */
export function barEndLabel(value, count, formatValue) {
  const v = typeof formatValue === 'function' ? formatValue(value) : String(value);
  const n = Number(count) || 0;
  return `${v} \u00b7 ${n}`;
}

/* ═══════════════════════════════════════════════════════════════════════════
 * Card P228 — THE 13-ROW FIXTURE WAS NOT REAL SCALE.
 *
 * P227 sized charts from their rows and proved it on a 13-stage fixture.
 * Daniel then ran ALL PIPELINES and got ~40 stages in a 664x240 chart: bars
 * one to two pixels tall, value labels piled into an unreadable block, and —
 * the part that makes it actively misleading — recharts AUTO-SKIPPING most
 * category ticks, so the names that did show sat next to bars they did not
 * belong to. The by-rep chart skipped every other tick: 1047, 1040 and 184
 * floating beside unlabelled bars.
 *
 * ⛔⛔ SIZING ALONE CANNOT FIX THIS. Forty rows at 28px is a 1,100px chart —
 * past the cap, and nobody reads a forty-row bar chart anyway. The answer is
 * fewer rows: the top ten by the measure the axis is drawn from, plus ONE
 * "Other (n)" row that sums the rest so the total still reconciles. This is
 * P222's "at most six series" rule applied to rows instead of lines.
 *
 * ⚠⚠ THE REMAINDER IS SUMMED, NEVER DROPPED — BUT IT IS A CAPTION, NOT A BAR.
 *
 * ⛔⛔ CARD P231, DANIEL'S CALL ON A FLAG I RAISED WITH THE P228 EVIDENCE. The
 * first version drew "Other (n)" as an eleventh bar. In the real data the
 * tail OUTWEIGHS the top ten — PEM Appt alone carries 566 unmapped opps — so
 * that bar ran the full width and compressed the ten rows the reader opened
 * the chart to read. A chart whose largest bar is "everything else" has
 * answered a question nobody asked.
 *
 * So the top ten are the bars, the x-axis is scaled to THEM, and the
 * remainder becomes one muted line underneath: "+30 more stages · $X · n opps
 * — see Table". The sum is still stated, so the reader still knows the chart
 * is a slice; the full list stays in the Table view and the CSV.
 * ═══════════════════════════════════════════════════════════════════════════ */

export const TOP_N = 10;

/**
 * Keep the top N rows by `valueOf`, and fold the rest into one "Other (n)".
 *
 * @param {Array}    rows
 * @param {Function} valueOf   row -> number (the measure the axis draws)
 * @param {Function} makeOther (count, summedValue, rest) -> the Other row
 * @param {number}   n
 */
export function topNWithRemainder(rows, valueOf, n = TOP_N) {
  const list = Array.isArray(rows) ? rows.slice() : [];
  const sorted = list.sort((a, b) => (Number(valueOf(b)) || 0) - (Number(valueOf(a)) || 0));
  if (sorted.length <= n) {
    return { shown: sorted, hidden: [], hiddenCount: 0, hiddenValue: 0 };
  }
  const shown = sorted.slice(0, n);
  const hidden = sorted.slice(n);
  return {
    shown,
    hidden,
    hiddenCount: hidden.length,
    hiddenValue: hidden.reduce((acc, r) => acc + (Number(valueOf(r)) || 0), 0),
  };
}

/**
 * The caption that stands in for the folded rows.
 *
 * ⚠ The NOUN is the caller's, because "+30 more" is not a sentence. A stage
 * chart says stages, a rep chart says reps, a source chart says sources —
 * the reader should not have to work out what was folded.
 */
export function foldCaption({ count, value, opps, noun, formatValue }) {
  if (!count) return '';
  const parts = [`+${count} more ${count === 1 ? noun.replace(/s$/, '') : noun}`];
  if (typeof formatValue === 'function' && Number.isFinite(Number(value))) {
    parts.push(formatValue(value));
  }
  if (Number.isFinite(Number(opps)) && opps > 0) {
    parts.push(`${opps} opp${opps === 1 ? '' : 's'}`);
  }
  return `${parts.join(' \u00b7 ')} — see Table`;
}

/**
 * Should this bar carry its value label?
 *
 * ⛔ A LABEL ON A ONE-PIXEL BAR IS NOISE. With `position="right"` the label
 * sits just past the bar's end, so for a near-zero value it lands hard against
 * the axis and collides with the row's own name. Below the threshold the
 * number is still reachable — the tooltip has it, and so does the table.
 *
 * ⚠ A FRACTION OF THE LARGEST BAR, not an absolute. "$500" is a big bar in a
 * chart whose maximum is $800 and invisible in one whose maximum is $2M.
 */
export function labelFits(value, maxValue, minFraction = 0.08) {
  const v = Number(value) || 0;
  const max = Number(maxValue) || 0;
  if (max <= 0) return false;
  return v / max >= minFraction;
}

/**
 * How wide a category axis must be to show its labels without clipping.
 *
 * ⛔⛔ CARD P234 — "oberto Barquero". The rep chart clipped its longest name
 * on the LEFT, losing the first letter. The cause is a mismatch between two
 * numbers that were never compared: truncateLabel() budgets TWO lines
 * (perLine * 2 characters), and recharts renders a category tick on ONE.
 * "Roberto Barquero" is 16 characters against a 28-character budget, so it
 * was never truncated at all — and then did not fit the 96px axis it was
 * measured against.
 *
 * ⚠ SO THE AXIS IS SIZED FROM THE LABELS, and the labels are then truncated
 * to THAT width at one line. Either alone leaves the gap: sizing without
 * truncating lets one long name eat the plot, truncating without sizing
 * keeps clipping whenever the estimate is optimistic.
 */
export function axisWidthFor(labels, { min = 96, max = 190, charWidth = 5.8 } = {}) {
  const list = (Array.isArray(labels) ? labels : []).map((l) => String(l == null ? '' : l));
  if (list.length === 0) return min;
  const longest = list.reduce((a, b) => (b.length > a.length ? b : a), '');
  /* +14: the tick's own left padding and the gap to the plot area. */
  const needed = Math.ceil(longest.length * charWidth) + 14;
  return Math.min(max, Math.max(min, needed));
}

/**
 * Does an end-of-bar label fit in the space to the right of its bar?
 *
 * ⛔ CARD P234 — "$176,112 · 109" WRAPPED onto two lines beside a short bar.
 * An end label must be ONE line: wrapped, it collides with the row below and
 * recreates the overlap P227 removed. labelFits() only asked whether the BAR
 * was wide enough; this asks whether the LABEL is short enough for the margin
 * it is drawn into. When it is not, the P228 rule applies — drop it, and let
 * the tooltip and the table carry the number.
 */
export function labelFitsMargin(label, marginPx, charWidth = 5.6) {
  const text = String(label == null ? '' : label);
  if (!text) return false;
  return Math.ceil(text.length * charWidth) <= (Number(marginPx) || 0) - 6;
}
